import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Alert,
  AlertDescription,
} from "../components/ui/alert";
import { AlertTriangle } from "lucide-react";
import logo2 from "../assets/logo2.svg";

/**
 * VerifyLoginOtpPage — SMS OTP step-up for a USSD-only user's first app login
 * (Option A+B, 2026-08-14).
 *
 * Shown after /login returns OTP_REQUIRED for a non-graduated USSD user who
 * entered their PIN. The backend sent a 6-digit OTP to the registered phone.
 * On success the backend returns APP_PASSWORD_REQUIRED + setAppPasswordToken,
 * and we route to /set-app-password to complete graduation.
 */
export function VerifyLoginOtpPage() {
  const navigate = useNavigate();
  const { pendingLoginOtp, verifyLoginOtp, isLoading } = useAuth();

  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // No active OTP session → back to login.
  if (!pendingLoginOtp) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!/^\d{6}$/.test(otp)) {
      setError("Please enter the 6-digit code we sent to your phone.");
      return;
    }

    setSubmitting(true);
    try {
      // On success this stashes pendingAppPassword in the context; route to
      // the password-set screen to finish graduation.
      await verifyLoginOtp(otp);
      navigate("/set-app-password");
    } catch (err: any) {
      setError(err?.message || "Invalid or expired code. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#86BE41] to-[#2D8CCA]">
      <div className="mobile-container">
        <div
          style={{
            padding: "var(--mobile-padding)",
            paddingTop: "2rem",
            paddingBottom: "2rem",
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-2">
              <img
                src={logo2}
                alt="MyMoolah Logo"
                className="h-16 w-auto"
                style={{ height: "6rem", width: "auto", maxWidth: "300px", objectFit: "contain" }}
              />
            </div>
            <h1
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "clamp(1.5rem, 4vw, 2rem)",
                fontWeight: "var(--font-weight-bold)",
                color: "white",
                marginBottom: "0.5rem",
              }}
            >
              Enter your login code
            </h1>
            <p
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "var(--mobile-font-base)",
                color: "rgba(255, 255, 255, 0.9)",
              }}
            >
              We sent a 6-digit code to your phone to confirm it's you. This is a one-time check for your first app login.
            </p>
          </div>

          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-xl" style={{ marginTop: "-2rem" }}>
            <CardHeader style={{ paddingBottom: "1rem" }}>
              <CardTitle
                className="text-center"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "clamp(1.125rem, 2.5vw, 1.25rem)",
                  fontWeight: "var(--font-weight-bold)",
                  color: "#1f2937",
                }}
              >
                Verify login
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert className="border-red-200 bg-red-50 mb-4">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      color: "#dc2626",
                    }}
                  >
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-small)", color: "#6b7280" }}>
                  Mobile number
                </p>
                <p style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-base)", fontWeight: "var(--font-weight-medium)", color: "#1f2937" }}>
                  {pendingLoginOtp.phoneNumber}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="otp"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "#374151",
                    }}
                  >
                    6-digit code
                  </Label>
                  <Input
                    id="otp"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Enter the 6-digit code"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                      if (error) setError("");
                    }}
                    className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] tracking-[0.3em] text-center"
                    style={{
                      height: "var(--mobile-touch-target)",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "var(--mobile-font-base)",
                      borderRadius: "var(--mobile-border-radius)",
                    }}
                    required
                    aria-describedby="otp-help"
                  />
                  <p
                    id="otp-help"
                    style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-small)", color: "#6b7280" }}
                  >
                    The code is valid for 10 minutes.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={otp.length !== 6 || submitting || isLoading}
                  className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] hover:from-[#7AB139] hover:to-[#2680B8] text-white disabled:opacity-60"
                  style={{
                    height: "var(--mobile-touch-target)",
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "var(--mobile-font-base)",
                    fontWeight: "var(--font-weight-medium)",
                    borderRadius: "var(--mobile-border-radius)",
                  }}
                >
                  {submitting ? "Verifying..." : "Verify & continue"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-xs text-[#2D8CCA] hover:text-[#2680B8] underline"
                  style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-small)" }}
                >
                  Back to sign in
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
